import React from "react"
import toast from "react-hot-toast"
import { useApp } from "../context/AppContext"
import { Icon } from "../shared"
import { Card, Btn, Badge } from "../shared/components"
import {
  getGameForLesson,
  startGameAttempt,
  reportStageResult,
  submitGameAttempt,
} from "../api/games.api"

/* =============================================================
   STANDALONE GAME ROUTE (page === "game")

   Mirrors the VideoPlayerScreen standalone-route pattern in
   LearningScreens.jsx, but lives in its own file since the 3D build is
   embedded via <iframe> rather than a React player component — see the
   plan doc for why GAME lessons route here instead of rendering inline in
   LessonFullPage (payload size + full-viewport interaction).

   JS <-> engine bridge (mode CLIENT, Godot/Unity): the running build posts
   { type: "stage_complete", stage_key, score, passed, time_sec } as each
   stage finishes, and { type: "scenario_complete" } once the whole
   scenario is done — this listens for both via window "message" and
   forwards them to the games API.

   Mode SERVER (skenario three.js bawaan, protokol v2): build mengirim
   { type: "scenario_ready" }; induk membalas { type: "init", attemptId, seed, lang }.
   Di akhir build mengirim { type: "scenario_complete", log }; induk mengirim log
   itu ke server (yang memutar ulang simulasi dan menghitung skor), lalu meneruskan
   skor resmi ke build lewat { type: "result", final, level, parts }. Build
   mengirim { type: "close" } saat peserta menekan "Selesai".
   ============================================================= */
function LessonGameScreen() {
  const { lang, navigate, pageState } = useApp()
  const iframeRef = React.useRef(null)

  const lessonId = pageState?.lessonId
  const lessonTitle = pageState?.lessonTitle || ""
  const courseId = pageState?.courseId
  // navigate() mengganti pageState seutuhnya, jadi courseId harus diteruskan
  // kembali — kalau tidak, peserta mendarat di kursus pertama program, bukan
  // kursus yang tadi ia tinggalkan.
  const kembaliKeKursus = () => navigate("course", courseId ? { courseId } : undefined)

  const [scenario, setScenario] = React.useState(null)
  const [loadError, setLoadError] = React.useState(null)
  const [attempt, setAttempt] = React.useState(null)
  const [iframeLoaded, setIframeLoaded] = React.useState(false)
  const [result, setResult] = React.useState(null)
  const [starting, setStarting] = React.useState(false)
  // Mode SERVER: skor resmi dari server, ditampilkan sebagai spanduk di atas iframe selama
  // peserta masih membaca debrief di dalam build; kartu hasil baru muncul setelah "Selesai".
  const [official, setOfficial] = React.useState(null)
  const [submitError, setSubmitError] = React.useState(null)
  const [submitting, setSubmitting] = React.useState(false)
  const pendingLog = React.useRef(null)

  // Laporan tahap yang masih dalam perjalanan. Tanpa menunggunya, penilaian
  // akhir bisa mendahului tahap terakhir yang baru saja dikirim — peserta yang
  // sebenarnya lulus dinilai atas stage_results yang belum lengkap.
  const laporanTahap = React.useRef(Promise.resolve())
  // Satu simulasi hanya boleh dinilai sekali; mesin yang mengirim
  // scenario_complete dua kali tidak boleh memunculkan galat 409 di layar
  // peserta yang sudah melihat hasilnya.
  const sudahDinilai = React.useRef(false)

  const load = React.useCallback(() => {
    if (!lessonId) {
      setLoadError("__no_lesson__")
      return
    }
    getGameForLesson(lessonId)
      .then(setScenario)
      .catch((e) => setLoadError(e?.response?.data?.error || e.message))
  }, [lessonId])

  React.useEffect(() => { load() }, [load])

  // Mengirim keputusan peserta ke server (mode SERVER) dan meneruskan skor resminya ke build.
  const kirimHasil = React.useCallback(async (attemptRef, log) => {
    setSubmitting(true); setSubmitError(null)
    try {
      const res = await submitGameAttempt(attemptRef.id, log)
      sudahDinilai.current = true
      setOfficial(res)
      iframeRef.current?.contentWindow?.postMessage({
        type: "result",
        final: res.finalScore,
        level: res.level,
        parts: (res.stageResults || []).map((x) => ({ key: x.stage_key, v: x.score })),
      }, "*")
    } catch (e) {
      sudahDinilai.current = false
      setSubmitError(e?.response?.data?.error || e.message)
    } finally {
      setSubmitting(false)
    }
  }, [])

  // JS <-> engine bridge. Scoped to messages from this attempt's iframe only.
  React.useEffect(() => {
    if (!attempt) return
    sudahDinilai.current = false
    laporanTahap.current = Promise.resolve()
    const serverScored = scenario?.scoring_mode === "SERVER"

    function onMessage(event) {
      if (!iframeRef.current || event.source !== iframeRef.current.contentWindow) return
      const msg = event.data || {}
      if (serverScored) {
        if (msg.type === "scenario_ready" && attempt.seed_value != null) {
          // targetOrigin "*" karena iframe ber-sandbox punya origin buram; yang menjaga
          // adalah pemeriksaan event.source di atas, dan pesan ini tidak memuat rahasia.
          iframeRef.current.contentWindow.postMessage(
            { type: "init", attemptId: attempt.id, seed: attempt.seed_value, lang },
            "*",
          )
        } else if (msg.type === "scenario_complete" && Array.isArray(msg.log)) {
          if (sudahDinilai.current) return
          pendingLog.current = msg.log
          kirimHasil(attempt, msg.log)
        } else if (msg.type === "close") {
          setOfficial((o) => { if (o) setResult(o); return o })
        }
        return
      }
      if (msg.type === "stage_complete") {
        laporanTahap.current = laporanTahap.current
          .then(() => reportStageResult(attempt.id, {
            stage_key: msg.stage_key,
            score: msg.score,
            passed: msg.passed,
            time_sec: msg.time_sec,
          }))
          .catch((e) => toast.error(e?.response?.data?.error || e.message))
      } else if (msg.type === "scenario_complete") {
        if (sudahDinilai.current) return
        sudahDinilai.current = true
        laporanTahap.current
          .then(() => submitGameAttempt(attempt.id))
          .then(setResult)
          .catch((e) => {
            sudahDinilai.current = false
            toast.error(e?.response?.data?.error || e.message)
          })
      }
    }
    window.addEventListener("message", onMessage)
    return () => window.removeEventListener("message", onMessage)
  }, [attempt, scenario, lang, kirimHasil])

  async function handleStart() {
    if (!lessonId || starting) return
    setStarting(true)
    try {
      const a = await startGameAttempt(lessonId)
      setAttempt(a)
      setIframeLoaded(false)
      setResult(null)
      setOfficial(null)
      setSubmitError(null)
      pendingLog.current = null
    } catch (err) {
      toast.error(err?.response?.data?.error || err.message)
    } finally {
      setStarting(false)
    }
  }

  function handleRetry() {
    setAttempt(null)
    setResult(null)
    setOfficial(null)
    setSubmitError(null)
    load()
  }

  function layarPenuh() {
    const el = iframeRef.current
    if (el?.requestFullscreen) el.requestFullscreen().catch(() => {})
  }

  if (loadError) {
    // Sebabnya diterjemahkan di sini, bukan disimpan sudah jadi kalimat:
    // pesannya harus ikut berubah saat peserta menukar bahasa.
    const sebab = loadError === "__no_lesson__"
      ? (lang === "id" ? "Pelajaran tidak ditentukan." : "No lesson specified.")
      : loadError
    return (
      <div className="main-inner">
        <Card>
          <h2 className="h2">{lang === "id" ? "Simulasi tidak dapat dimuat" : "Could not load simulation"}</h2>
          <p className="muted small mt-2">{sebab}</p>
          <Btn variant="secondary" className="mt-3" onClick={kembaliKeKursus}>
            {lang === "id" ? "Kembali ke kursus" : "Back to course"}
          </Btn>
        </Card>
      </div>
    )
  }
  if (!scenario) {
    return <div className="main-inner"><Card tight><p className="muted">{lang === "id" ? "Memuat simulasi…" : "Loading simulation…"}</p></Card></div>
  }

  const noBuild = !scenario.buildUrl
  const noAttemptsLeft = scenario.remainingAttempts === 0
  // null = tak terbatas. Percobaan yang sedang/baru saja berjalan belum
  // terhitung di angka dari server, jadi dikurangi sendiri di sini.
  const sisaPercobaanSetelahIni = scenario.remainingAttempts == null
    ? null
    : Math.max(0, scenario.remainingAttempts - (attempt ? 1 : 0))

  return (
    <div className="main-inner" style={{ maxWidth: "none", height: "100%", display: "flex", flexDirection: "column" }}>
      <div className="row between" style={{ marginBottom: 16, flexWrap: "wrap", gap: 8 }}>
        <div className="row gap-2" style={{ alignItems: "center" }}>
          <Btn variant="ghost" size="sm" icon={<Icon name="chevron_left" size={14} />} onClick={kembaliKeKursus}>
            {lang === "id" ? "Kembali" : "Back"}
          </Btn>
          <span className="muted" style={{ fontSize: 12 }}>{scenario.title || lessonTitle}</span>
        </div>
        <div className="row gap-2" style={{ alignItems: "center" }}>
          {attempt && !result && (
            <Btn variant="ghost" size="sm" onClick={layarPenuh}>{lang === "id" ? "Layar penuh" : "Fullscreen"}</Btn>
          )}
          <span className="tiny muted">
            {lang === "id" ? "Lulus min." : "Pass min."} {scenario.mastery_threshold}%
            {scenario.max_attempts != null && (
              <> · {lang === "id" ? "Percobaan tersisa" : "Attempts left"}: {scenario.remainingAttempts}/{scenario.max_attempts}</>
            )}
          </span>
        </div>
      </div>

      {result ? (
        <Card>
          <h2 className="h2">{scenario.title}</h2>
          <div className="row gap-3 mt-3">
            <div style={{
              padding: 16,
              background: result.passed ? "var(--green-10, #E8F5E9)" : "var(--red-10, #FFEBEE)",
              border: "1px solid",
              borderColor: result.passed ? "var(--green-50, #2E7D32)" : "var(--red-50, #C62828)",
              flex: 1,
            }}>
              <div style={{ fontWeight: 600, fontSize: 18 }}>
                {result.passed ? (lang === "id" ? "Lulus 🎉" : "Passed 🎉") : (lang === "id" ? "Belum lulus" : "Not yet passed")}
              </div>
              <div className="small muted mt-1">
                {lang === "id" ? "Skor akhir" : "Final score"}: <strong>{Math.round(result.finalScore)}%</strong>
                {" · "}{lang === "id" ? "Lulus min." : "Pass min."} {result.masteryThreshold}%
              </div>
              {result.lessonCompleted && (
                <div className="tiny mt-2" style={{ color: "var(--green-60)" }}>
                  ✓ {lang === "id" ? "Pelajaran ditandai selesai" : "Lesson marked complete"}
                </div>
              )}
              {result.stageResults?.length > 0 && (
                <div className="col gap-1 mt-3">
                  {result.stageResults.map((s) => (
                    <div key={s.stage_key} className="row gap-2" style={{ alignItems: "center" }}>
                      <Badge tone={s.passed ? "success" : "danger"} dot>{Math.round(s.score)}%</Badge>
                      <span className="tiny muted">{s.stage_key}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          <div className="row gap-2 mt-4">
            <Btn variant="secondary" onClick={kembaliKeKursus}>{lang === "id" ? "Kembali" : "Back"}</Btn>
            {/* scenario.remainingAttempts berasal dari pemuatan SEBELUM
                percobaan ini berjalan, jadi percobaan yang baru saja dipakai
                belum terhitung — dikurangi satu di sini supaya "Coba lagi"
                tidak ditawarkan padahal jatahnya sudah habis. */}
            {!result.passed && sisaPercobaanSetelahIni !== 0 && (
              <Btn variant="ghost" onClick={handleRetry}>{lang === "id" ? "Coba lagi" : "Try again"}</Btn>
            )}
          </div>
        </Card>
      ) : attempt ? (
        <>
        {scenario.scoring_mode === "SERVER" && (submitting || submitError || official) && (
          <div className={"banner " + (submitError ? "amber" : "")} style={{ marginBottom: 10 }}>
            <div className="row between" style={{ gap: 8, flexWrap: "wrap", alignItems: "center" }}>
              <span className="small">
                {submitting && (lang === "id" ? "Server sedang menghitung skor resmi Anda…" : "The server is calculating your official score…")}
                {submitError && (<>{lang === "id" ? "Skor resmi belum bisa dihitung: " : "Could not calculate the official score: "}{submitError}</>)}
                {official && !submitting && !submitError && (
                  <>
                    {lang === "id" ? "Skor resmi" : "Official score"}: <strong>{Math.round(official.finalScore)}%</strong>
                    {" · "}{official.passed ? (lang === "id" ? "Lulus" : "Passed") : (lang === "id" ? "Belum lulus" : "Not yet passed")}
                    {official.lessonCompleted && <> · ✓ {lang === "id" ? "Pelajaran ditandai selesai" : "Lesson marked complete"}</>}
                  </>
                )}
              </span>
              {submitError && pendingLog.current && (
                <Btn variant="secondary" size="sm" disabled={submitting} onClick={() => kirimHasil(attempt, pendingLog.current)}>
                  {lang === "id" ? "Kirim ulang" : "Retry"}
                </Btn>
              )}
              {official && (
                <Btn variant="secondary" size="sm" onClick={() => setResult(official)}>
                  {lang === "id" ? "Lihat ringkasan" : "View summary"}
                </Btn>
              )}
            </div>
          </div>
        )}
        <div style={{ flex: 1, minHeight: "min(480px, 70dvh)", position: "relative", border: "1px solid var(--border-subtle)", borderRadius: 10, overflow: "hidden", background: "#0B1417" }}>
          {!iframeLoaded && (
            <div style={{ position: "absolute", inset: 0, display: "grid", placeItems: "center", color: "white" }}>
              <p className="muted">{lang === "id" ? "Memuat build simulasi… (berkas besar, mohon tunggu)" : "Loading simulation build… (large payload, please wait)"}</p>
            </div>
          )}
          {/*
            sandbox tanpa allow-same-origin — ini yang menjaganya.

            Di produksi /games disajikan dari host yang sama dengan aplikasi
            (lihat deploy/Caddyfile), jadi tanpa sandbox skrip di dalam build
            bisa menjangkau parent.localStorage dan membaca token sesi peserta.
            Build-nya diunggah pelatih, bukan ditulis kami; ia tidak boleh
            dipercaya sejauh itu.

            allow-scripts saja sudah cukup untuk WASM, dan postMessage tetap
            berfungsi lintas origin buram. Konsekuensinya build tidak punya
            akses localStorage/IndexedDB sendiri — untuk simulasi yang
            melaporkan hasilnya lewat postMessage, itu memang tidak diperlukan.
          */}
          <iframe
            ref={iframeRef}
            src={`${import.meta.env.VITE_API_URL || ""}${scenario.buildUrl}${scenario.buildUrl.includes("?") ? "&" : "?"}lang=${lang}`}
            title={scenario.title}
            onLoad={() => setIframeLoaded(true)}
            sandbox="allow-scripts"
            allow="fullscreen"
            style={{ width: "100%", height: "100%", border: "none", display: "block" }}
          />
        </div>
        </>
      ) : (
        <Card>
          <h2 className="h2">{scenario.title}</h2>
          {noBuild ? (
            <p className="muted mt-2">
              {lang === "id" ? "Build simulasi belum diunggah trainer untuk pelajaran ini." : "The trainer hasn't uploaded a simulation build for this lesson yet."}
            </p>
          ) : noAttemptsLeft ? (
            <div className="banner amber mt-2">
              <span className="small">
                {lang === "id" ? `Anda sudah memakai semua ${scenario.max_attempts} percobaan.` : `You have used all ${scenario.max_attempts} attempts.`}
                {scenario.lastAttempt && (<> {lang === "id" ? "Skor terakhir" : "Last score"}: <strong>{Math.round(scenario.lastAttempt.final_score ?? 0)}%</strong>.</>)}
              </span>
            </div>
          ) : (
            <>
              <p className="muted mt-2">
                {lang === "id"
                  ? "Simulasi 3D interaktif — selesaikan tiap tahap sesuai prosedur untuk lulus. Anda bisa mengulang tahap yang gagal tanpa mengulang dari awal."
                  : "Interactive 3D simulation — clear each stage per procedure to pass. Failed stages can be retried without restarting."}
              </p>
              <Btn variant="primary" className="mt-3" disabled={starting} onClick={handleStart}>
                {starting ? (lang === "id" ? "Memulai…" : "Starting…") : (lang === "id" ? "Mulai Simulasi" : "Start Simulation")}
              </Btn>
            </>
          )}
        </Card>
      )}
    </div>
  )
}

export { LessonGameScreen }
